<template>
  <v-card :disabled="loading" :loading="loading">
    <v-card-title>
      <h2>{{ $t("menu.config_project_group") }}</h2>
    </v-card-title>
    <v-card-text>
      <v-tabs v-model="level_tab">
        <v-tabs-slider color="yellow"></v-tabs-slider>
        <v-tab v-for="(level, i) in levels" :key="i">
          <h3>{{ objectTranslator(level, "LEVEL_NAME") }}</h3>
        </v-tab>
      </v-tabs>

      <v-tabs-items v-model="level_tab" touchless>
        <v-tab-item v-for="(level, i) in levels" :key="i">
          <v-card flat>
            <v-card-text>
              <v-autocomplete
                v-model="year_id[level.LEVEL_ID]"
                v-bind:label="$t('officer_config.academic_year')"
                v-bind:items="
                  listFilter(config_years, level.LEVEL_ID, 'LEVEL_ID')
                "
                v-bind:item-value="(item) => item.YEAR_ID"
                v-bind:item-text="(item) => item.SEMESTER + ' / ' + item.YEAR"
                outlined
                @change="(year_id) => select_year(level.LEVEL_ID, year_id)"
              ></v-autocomplete>

              <div v-show="year_id && year_id[level.LEVEL_ID]">
                <v-autocomplete
                  v-model="round_id[level.LEVEL_ID]"
                  v-bind:label="$t('officer_config.round_number_name')"
                  v-bind:items="rounds[level.LEVEL_ID]"
                  v-bind:item-value="(item) => item.ROUND_ID"
                  v-bind:item-text="
                    (item) =>
                      item.ROUND_NUMBER + ' - ' + objectTranslator(item, 'NAME')
                  "
                  outlined
                  @change="(round_id) => select_round(level.LEVEL_ID, round_id)"
                ></v-autocomplete>

                <div v-show="round_id && round_id[level.LEVEL_ID]">
                  <v-btn
                    color="success"
                    class="mb-5 mt-4"
                    @click="
                      () =>
                        open_add_dialog(
                          round_id[level.LEVEL_ID],
                          level.LEVEL_ID,
                          year_id[level.LEVEL_ID]
                        )
                    "
                  >
                    <v-icon>mdi-plus</v-icon>{{ $t("officer_config.addnew") }}
                  </v-btn>
                  <div
                    class="mb-5"
                    v-for="(project_group, index) in listFilter(
                      project_groups[level.LEVEL_ID],
                      round_id[level.LEVEL_ID],
                      'ROUND_ID'
                    )"
                    :key="project_group.ID"
                  >
                    <v-divider class="mt-5 mb-5"></v-divider>

                    <h2 class="mb-2">
                      {{ index + 1 }}.
                      {{ objectTranslator(project_group, "NAME") }}
                      <v-btn
                        color="blue"
                        dark
                        small
                        elevation="1"
                        @click="
                          $refs.addEditDialog.open_edit_modal({
                            item: project_group,
                            level_id: level.LEVEL_ID,
                          })
                        "
                        >edit</v-btn
                      >
                      <v-btn
                        class="ml-3"
                        color="red"
                        dark
                        outlined
                        small
                        elevation="1"
                        @click="
                          open_delete_modal(
                            project_group,
                            level.LEVEL_ID,
                            listFilter(
                              projects[level.LEVEL_ID],
                              project_group.ID,
                              'GROUP_ID'
                            )
                          )
                        "
                      >
                        <v-icon>mdi-trash-can</v-icon>
                      </v-btn>
                    </h2>
                    <div
                      class="ml-5"
                      v-for="(project, sub_index) in listFilter(
                        projects[level.LEVEL_ID],
                        project_group.ID,
                        'GROUP_ID'
                      )"
                      :key="project.ID"
                    >
                      {{ sub_index + 1 }}.
                      {{ objectTranslator(project, "PROJECT_NAME") }}
                    </div>
                  </div>
                </div>
              </div>
            </v-card-text>
          </v-card>
        </v-tab-item>
      </v-tabs-items>
    </v-card-text>

    <v-dialog v-model="showing_add_modal" max-width="600">
      <v-card>
        <v-card-title>{{ $t("officer_config.addnew") }}</v-card-title>
        <v-card-text>
          <v-form ref="form">
            <v-text-field
              v-model="group_name_th"
              :label="$t('menu.config_project_group_name_th')"
              outlined
              dense
              :rules="requiredRules"
            ></v-text-field>
            <v-text-field
              v-model="group_name_en"
              :label="$t('menu.config_project_group_name_en')"
              outlined
              dense
              :rules="requiredRules"
            ></v-text-field>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-btn dark elevation="0" color="green" @click="save_new">Save</v-btn>
          <v-btn
            elevation="0"
            outlined
            color="red"
            @click="showing_add_modal = false"
            >Close</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>

    <ConfigProjectGroupAddEditDialog
      ref="addEditDialog"
      :toggle_loading="toggle_loading"
      :fetch_data="fetch_data"
    />

    <ConfigDeleteDialog
      v-model="showing_delete_modal"
      :title="$t('officer_config.config_project')"
      :text="
        to_delete_group_item
          ? ' ' + objectTranslator(to_delete_group_item, 'NAME')
          : ''
      "
      :deleteFunction="delete_config_project_group"
    />
  </v-card>
</template>

<script>
import { createHelpers } from "vuex-map-fields";
import ConfigProjectGroupAddEditDialog from "@/components/config/ConfigProjectGroupAddEditDialog";
import ConfigDeleteDialog from "@/components/config/ConfigDeleteDialog";
import {
  objectTranslator,
  dateFromDBTranslator as dt,
} from "@/helper/translator";
import { listFilter, getById } from "@/helper/filter";
import ConfigProvider from "@/services/config.provider";
import validator from "@/helper/validator";

const configService = new ConfigProvider();
const { mapFields: mapOfficerConfigFields } = createHelpers({
  getterType: "officerConfig/getField",
  mutationType: "officerConfig/updateField",
});

export default {
  name: "ConfigProjectGroup",
  data: function() {
    return {
      group_name_en: "",
      group_name_th: "",

      loading: false,
      level_tab: null,
      showing_add_modal: false,
      showing_delete_modal: false,

      adding_group_round_id: null,
      adding_group_level_id: null,
      adding_group_year_id: null,

      to_delete_group_id: null,
      to_delete_round_id: null,
      to_delete_level_id: null,
      to_delete_year_id: null,
      to_delete_group_item: null,
      to_delete_projects: [],
      // to_delete_project_group_item: null,

      //--- selected data in each level panel
      rounds: {}, // each for level id
      projects: {}, // each for level id
      project_groups: {}, // each for level id
      year_id: {}, // each for level id
      round_id: {}, // each for level id

      requiredRules: [validator.required],
    };
  },
  components: {
    ConfigProjectGroupAddEditDialog,
    ConfigDeleteDialog,
  },
  computed: {
    levels() {
      return this.$store.state.ref.levels;
    },
    ...mapOfficerConfigFields({
      config_years: "config_years",
    }),
  },
  methods: {
    dt,
    getById,
    listFilter,
    objectTranslator,
    toggle_loading(status) {
      this.loading = status;
    },

    async fetch_ref_data(level_id, year_id) {
      // non editable data of this page
      this.loading = true;
      const rounds = await configService.getFilteredRounds({ year_id });
      this.$set(this.rounds, level_id, rounds);
      this.loading = false;
    },
    async fetch_data(level_id, year_id) {
      // editable data of this page
      this.loading = true;
      const projects = await configService.getFilteredProjects({ year_id });
      this.$set(this.projects, level_id, projects);
      const project_groups = await configService.getProjectGroup({ year_id });
      this.$set(this.project_groups, level_id, project_groups);
      this.loading = false;
    },

    async select_year(level_id, year_id) {
      this.round_id[level_id] = null;
      await this.fetch_ref_data(level_id, year_id);
      await this.fetch_data(level_id, year_id);
    },

    async select_round(level_id, round_id) {
      this.round_id[level_id] = round_id;
      await this.fetch_data(level_id, this.year_id[level_id]);
    },

    async save_new() {
      if (this.$refs.form.validate()) {
        this.loading = true;
        const params = {
          name_en: this.group_name_en,
          name_th: this.group_name_th,
          round_id: this.adding_group_round_id,
        };
        const response = await configService
          .addNewProjectGroup(params)
          .catch((error) => {
            this.$toasted.error(JSON.stringify(error.response.data.message), {
              duration: 0,
            });
            this.loading = false;
          });
        if (response) {
          if (response.status) {
            this.$toasted.success(this.$t("toasted.edit_successfully"));
            await this.fetch_data(
              this.adding_group_level_id,
              this.adding_group_year_id
            );
            this.showing_add_modal = false;
          } else {
            this.$toasted.error(this.$t("toasted.cannot_edit"));
          }
        }

        this.loading = false;
      }
    },

    open_delete_modal(item, level_id, to_delete_projects) {
      this.to_delete_group_id = item.ID;
      this.to_delete_round_id = item.ROUND_ID;
      this.to_delete_level_id = level_id;
      this.to_delete_year_id = item.YEAR_ID;
      this.to_delete_group_item = item;
      this.to_delete_projects = to_delete_projects;
      this.showing_delete_modal = true;
    },
    async delete_config_project_group() {
      this.loading = true;

      for (const item of this.to_delete_projects) {
        await configService
          .editGroupOfProject({
            project_id: item.PROJECT_ID,
            group_id: null,
          })
          .catch((error) => {
            this.$toasted.error(JSON.stringify(error.response.data.message), {
              duration: 0,
            });
            this.loading = false;
          });
      }

      let response = await configService
        .deleteProjectGroup({
          id: this.to_delete_group_id,
        })
        .catch((error) => {
          this.$toasted.error(JSON.stringify(error.response.data.message), {
            duration: 0,
          });
          this.loading = false;
        });

      this.fetch_data(this.to_delete_level_id, this.to_delete_year_id);

      if (response && response.status)
        this.$toasted.success(this.$t("toasted.delete_successfully"));
      else this.$toasted.error(this.$t("toasted.cannot_delete"));

      this.showing_delete_modal = false;
      this.loading = false;
    },

    open_add_dialog(round_id, level_id, year_id) {
      this.adding_group_round_id = round_id;
      this.adding_group_level_id = level_id;
      this.adding_group_year_id = year_id;
      this.showing_add_modal = true;
    },
  },
  created: function() {
    this.$store.dispatch("ref/getLevels");
    this.$store.dispatch("officerConfig/getYears");
  },
};
</script>
